import { ENV } from "../utils";

export class ObjectiveMatrix {
  baseApi = ENV.BASE_API;

  // ============================================
  // CRUD BÁSICO
  // ============================================

  /**
   * Crear nueva matriz de objetivos
   */
  async createMatrix(accessToken, data) {
    try {
      const url = `${this.baseApi}/objective-matrix`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 201) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener todas las matrices (con paginación)
   */
  async getMatrices(accessToken, queryParams = {}) {
    try {
      const { page = 1, limit = 10, active = true } = queryParams;
      const url = `${this.baseApi}/objective-matrix?page=${page}&limit=${limit}&active=${active}`;

      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener matriz por ID
   */
  async getMatrixById(accessToken, id) {
    try {
      const url = `${this.baseApi}/objective-matrix/${id}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualizar matriz completa
   */
  async updateMatrix(accessToken, id, data) {
    try {
      const url = `${this.baseApi}/objective-matrix/${id}`;
      const params = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Eliminar matriz (soft delete)
   */
  async deleteMatrix(accessToken, id) {
    try {
      const url = `${this.baseApi}/objective-matrix/${id}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  // ============================================
  // POR SITIO
  // ============================================

  /**
   * Obtener matrices por sitio
   */
  async getMatricesBySite(accessToken, siteId, active = true) {
    try {
      const url = `${this.baseApi}/objective-matrix/site/${siteId}?active=${active}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener matriz por sitio y año
   */
  async getMatrixBySiteAndYear(accessToken, siteId, year) {
    try {
      const url = `${this.baseApi}/objective-matrix/site/${siteId}/year/${year}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  // ============================================
  // OPERACIONES SOBRE OBJETIVOS
  // ============================================

  /**
   * Agregar objetivo a una matriz
   */
  async addObjective(accessToken, matrixId, objectiveData) {
    try {
      const url = `${this.baseApi}/objective-matrix/${matrixId}/objective`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(objectiveData),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 201) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualizar objetivo específico
   */
  async updateObjective(accessToken, matrixId, objectiveId, objectiveData) {
    try {
      const url = `${this.baseApi}/objective-matrix/${matrixId}/objective/${objectiveId}`;
      const params = {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(objectiveData),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Eliminar objetivo específico
   */
  async deleteObjective(accessToken, matrixId, objectiveId) {
    try {
      const url = `${this.baseApi}/objective-matrix/${matrixId}/objective/${objectiveId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  // ============================================
  // DASHBOARD & ANALYTICS
  // ============================================

  /**
   * Obtener datos del dashboard
   */
  async getDashboardData(accessToken, matrixId) {
    try {
      const url = `${this.baseApi}/objective-matrix/${matrixId}/dashboard`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }

  // ============================================
  // IMPORT / EXPORT
  // ============================================

  /**
   * Exportar matriz a CSV
   */
  async exportToCSV(accessToken, matrixId) {
    try {
      const url = `${this.baseApi}/objective-matrix/${matrixId}/export-csv`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);

      if (response.status !== 200) {
        const result = await response.json();
        throw result;
      }

      // Descargar el archivo CSV
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `Matriz_Objetivos_${matrixId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Importar matriz desde CSV
   */
  async importFromCSV(accessToken, data) {
    try {
      const url = `${this.baseApi}/objective-matrix/import-csv`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 201) throw result;

      return result;
    } catch (error) {
      throw error;
    }
  }
}
